/**
 * Finnhub markets adapter — implements ProviderAdapter<MarketsParams, RawFinnhubQuote, MarketsData>.
 *
 * CLIENT / SERVER SPLIT (ADR-002 §5):
 *   CLIENT-SIDE:  paramsSchema, dataShape, cacheKey, glanceView
 *   SERVER-SIDE:  buildRequest, responseSchema, normalize
 *
 * `buildRequest` is THE ONLY place a URL exists in the whole system.
 * API keys are INJECTED as a parameter (never hardcoded, never logged).
 *
 * Finnhub API docs: https://finnhub.io/docs/api/quote
 * Quote endpoint: GET https://finnhub.io/api/v1/quote?symbol=AAPL&token=<key>
 *
 * V1 LIMITATION (prominently documented):
 *   The `params.symbols` basket may contain 1–12 symbols, but Finnhub's /quote
 *   endpoint is PER-SYMBOL (one HTTP call per symbol). In v1, this adapter fetches
 *   and represents ONLY THE FIRST symbol in the basket. The full basket is preserved
 *   in the cache key so the upgrade path to multi-symbol is clean: a future v2
 *   adapter can fan out calls per symbol without changing the key format.
 *
 *   WHY: Fan-out across 12 symbols in a single request would require batching,
 *   error aggregation, and a multi-symbol data shape — all of which are Pro scope.
 *   The free-tier use case is "show me AAPL" with an optional watchlist in the config
 *   that the tile renders in a list variant.
 *
 * Identity:
 *   type     = 'markets.quote'
 *   provider = 'finnhub'
 *   tier     = 'free'
 *   ttlSec   = 60 (60s delayed quotes per registry ADR-001 §3)
 */

import { z } from 'zod';
import { marketsParamsSchema, deriveCacheKey, type MarketsParams } from 'briefpane/schema';
import type { GlanceMap } from 'briefpane/schema';
import type { GlanceViewModel, ProviderAdapter, UpstreamRequest } from 'briefpane/contracts';
import { rawFinnhubQuoteSchema, normalize, type RawFinnhubQuote, type MarketsData } from './normalize.js';
import { glanceView } from './glance.js';

// ---------------------------------------------------------------------------
// dataShape Zod schema
// Keys must match DATA_SHAPE_KEYS['markets.quote']:
//   price, change, changePct, direction, currency, prevClose
// ---------------------------------------------------------------------------

const marketsDataShape = z.object({
  price: z.number(),
  change: z.number(),
  changePct: z.number(),
  direction: z.enum(['up', 'down', 'flat']),
  currency: z.string(),
  prevClose: z.number().nullable(),
});

// ---------------------------------------------------------------------------
// The adapter object
// ---------------------------------------------------------------------------

/**
 * The markets adapter with a build-request signature that accepts an injected API key.
 *
 * Extended from ProviderAdapter to allow key injection without hardcoding.
 */
export interface MarketsAdapterShape
  extends Omit<ProviderAdapter<MarketsParams, RawFinnhubQuote, MarketsData>, 'buildRequest'> {
  /** Server-side: build the Finnhub upstream request, injecting the API key. */
  buildRequest(params: MarketsParams, apiKey: string): UpstreamRequest;
  /** Map normalized data + glance map to the H3 view-model. */
  glanceView(data: MarketsData, glance: GlanceMap): GlanceViewModel;
}

/**
 * The Finnhub markets quote adapter.
 *
 * V1 LIMITATION: fetches ONLY the first symbol in the basket.
 * See module-level doc above for rationale and upgrade path.
 */
export const marketsAdapter: MarketsAdapterShape = {
  // ---- Identity (both halves) ----
  type: 'markets.quote',
  provider: 'finnhub',
  tier: 'free',
  ttlSec: 60,

  // ---- CLIENT-SIDE ----
  paramsSchema: marketsParamsSchema,
  dataShape: marketsDataShape,

  cacheKey: (params: MarketsParams): string =>
    deriveCacheKey({ type: 'markets.quote', provider: 'finnhub', params }),

  glanceView: (data: MarketsData, glance: GlanceMap): GlanceViewModel =>
    glanceView(data, glance),

  // ---- SERVER-SIDE ONLY ----
  responseSchema: rawFinnhubQuoteSchema,

  /**
   * Build the Finnhub /quote request for the FIRST symbol in the basket.
   *
   * THE ONLY PLACE A URL EXISTS IN THE SYSTEM (ADR-002 §5, §7 #1).
   * Never call this client-side. The `apiKey` is injected from server env.
   * Never log the returned URL — it contains the key.
   *
   * V1 LIMITATION: Only `params.symbols[0]` is used. The rest of the basket
   * is preserved in the cache key but not fetched. See module doc.
   *
   * Finnhub /quote: https://finnhub.io/docs/api/quote
   * Params: symbol (required), token (required, = apiKey)
   */
  buildRequest: (params: MarketsParams, apiKey: string): UpstreamRequest => ({
    // V1: fetch only the first symbol. See module-level V1 LIMITATION note.
    url: `https://finnhub.io/api/v1/quote?symbol=${params.symbols[0]}&token=${apiKey}`,
    method: 'GET',
  }),

  normalize: (raw: RawFinnhubQuote): MarketsData => normalize(raw),
};
